import { classifyStatus, PlaybackError } from './errors';
import { fetchWithCredential, joinUrl } from './http';
import { type CredentialSource, type MatchLiveStatus } from './types';

export interface CatalogMatch {
  matchUrn: string;
  matchName: string;
  status: MatchLiveStatus;
  datePlannedStart?: string;
  tournamentUrn: string;
  tournamentName: string;
  sport: string;
}

export interface CatalogTournament {
  urn: string;
  name: string;
  sport: string;
  isOffline: boolean;
  matches: CatalogMatch[];
}

export interface Catalog {
  tournaments: CatalogTournament[];
  /** ETag for a cheap conditional refresh next time. */
  etag?: string;
  /** True when the server returned 304 (the prior etag is still fresh); tournaments is empty. */
  notModified: boolean;
}

export interface FetchCatalogOptions {
  baseUrl: string;
  credential: CredentialSource;
  signal?: AbortSignal;
  /** Prior ETag for a conditional GET (304 when unchanged). */
  etag?: string;
  status?: MatchLiveStatus | MatchLiveStatus[];
  sport?: string;
  /**
   * Extra headers attached to every request to {@link baseUrl} — and only
   * there: never to CDN or license hosts. `x-api-key` cannot be overridden.
   */
  apiHeaders?: Record<string, string>;
}

interface RawCatalog {
  tournaments?: Array<{
    urn?: string;
    name?: string;
    sport?: string;
    isOffline?: boolean;
    matches?: Array<{
      matchUrn?: string;
      matchName?: string;
      status?: string;
      datePlannedStart?: string;
    }>;
  }>;
}

/** GET /v1/catalog — metadata only (no manifest/DRM). Supports conditional refresh. */
export async function fetchCatalog(opts: FetchCatalogOptions): Promise<Catalog> {
  const params = new URLSearchParams();
  if (opts.status) {
    params.set('status', Array.isArray(opts.status) ? opts.status.join(',') : opts.status);
  }
  if (opts.sport) params.set('sport', opts.sport);
  const qs = params.toString();
  const url = joinUrl(opts.baseUrl, '/v1/catalog' + (qs ? `?${qs}` : ''));

  const headers = new Headers();
  if (opts.etag) headers.set('If-None-Match', opts.etag);

  const res = await fetchWithCredential(url, {
    credential: opts.credential,
    signal: opts.signal,
    headers,
    apiHeaders: opts.apiHeaders,
  });

  if (res.status === 304) {
    return { tournaments: [], etag: opts.etag, notModified: true };
  }
  if (!res.ok) {
    throw new PlaybackError(
      classifyStatus(res.status),
      res.status,
      `catalog request failed (HTTP ${res.status})`,
    );
  }

  const raw = (await res.json()) as RawCatalog;
  return {
    tournaments: parseCatalog(raw),
    etag: res.headers.get('etag') ?? undefined,
    notModified: false,
  };
}

function parseCatalog(raw: RawCatalog): CatalogTournament[] {
  return (raw.tournaments ?? []).map((t) => {
    const urn = t.urn ?? '';
    const name = t.name ?? '';
    const sport = t.sport ?? '';
    return {
      urn,
      name,
      sport,
      isOffline: Boolean(t.isOffline),
      matches: (t.matches ?? []).map((m) => ({
        matchUrn: m.matchUrn ?? '',
        matchName: m.matchName ?? '',
        status: (m.status as MatchLiveStatus) ?? 'upcoming',
        datePlannedStart: m.datePlannedStart,
        tournamentUrn: urn,
        tournamentName: name,
        sport,
      })),
    };
  });
}

export interface FetchTournamentOptions {
  baseUrl: string;
  credential: CredentialSource;
  /** Tournament URN, e.g. `od:tournament:42`. */
  urn: string;
  signal?: AbortSignal;
  /** Prior ETag for a conditional GET (304 when unchanged) — only effective once havik-streams ETags `/v1/tournaments/{urn}` (parity with `/v1/matches/{urn}`). */
  etag?: string;
  /**
   * Extra headers attached to every request to {@link baseUrl} — and only
   * there: never to CDN or license hosts. `x-api-key` cannot be overridden.
   */
  apiHeaders?: Record<string, string>;
}

export interface TournamentResult {
  /** Present unless the tournament is unknown (404) or the server returned 304. */
  tournament?: CatalogTournament;
  /** ETag for a cheap conditional refresh next time, when the server emits one. */
  etag?: string;
  /** True when the server returned 304 (the prior etag is still fresh); `tournament` is undefined. */
  notModified: boolean;
  /** True when the URN is not in the catalog (or unknown to the caller's brand). */
  notFound: boolean;
}

interface RawTournament {
  urn?: string;
  name?: string;
  sport?: string;
  isOffline?: boolean;
  matches?: Array<{
    matchUrn?: string;
    matchName?: string;
    status?: string;
    datePlannedStart?: string;
  }>;
}

/**
 * `GET /v1/tournaments/{urn}` — single-tournament metadata (matches included,
 * never any signed/per-viewer URLs; those live on `/v1/playback/{urn}`).
 *
 * Returns `notFound: true` on 404 (unknown URN OR caller-not-entitled — the
 * server intentionally collapses both so existence isn't leaked). Other waitable
 * statuses (5xx) reject as a typed `PlaybackError`. Supports ETag/304.
 */
export async function fetchTournament(opts: FetchTournamentOptions): Promise<TournamentResult> {
  const url = joinUrl(opts.baseUrl, `/v1/tournaments/${encodeURIComponent(opts.urn)}`);
  const headers = new Headers();
  if (opts.etag) headers.set('If-None-Match', opts.etag);

  const res = await fetchWithCredential(url, {
    credential: opts.credential,
    signal: opts.signal,
    headers,
    apiHeaders: opts.apiHeaders,
  });

  if (res.status === 304) {
    return { etag: opts.etag, notModified: true, notFound: false };
  }
  if (res.status === 404) {
    return { notModified: false, notFound: true };
  }
  if (!res.ok) {
    throw new PlaybackError(
      classifyStatus(res.status),
      res.status,
      `tournament request failed (HTTP ${res.status})`,
    );
  }

  const raw = (await res.json()) as RawTournament;
  return {
    tournament: parseTournament(raw),
    etag: res.headers.get('etag') ?? undefined,
    notModified: false,
    notFound: false,
  };
}

function parseTournament(raw: RawTournament): CatalogTournament {
  const urn = raw.urn ?? '';
  const name = raw.name ?? '';
  const sport = raw.sport ?? '';
  return {
    urn,
    name,
    sport,
    isOffline: Boolean(raw.isOffline),
    matches: (raw.matches ?? []).map((m) => ({
      matchUrn: m.matchUrn ?? '',
      matchName: m.matchName ?? '',
      status: (m.status as MatchLiveStatus) ?? 'upcoming',
      datePlannedStart: m.datePlannedStart,
      tournamentUrn: urn,
      tournamentName: name,
      sport,
    })),
  };
}

/** Flatten a Catalog to a single match list (handy for grids). */
export function flattenMatches(catalog: Catalog): CatalogMatch[] {
  return catalog.tournaments.flatMap((t) => t.matches);
}
